import {
  DashboardResponse,
  GuestServicesDashboardData,
} from '../../../types/dashboard';
import { ConciergeBell, Megaphone, UtensilsCrossed, Store } from 'lucide-react';

interface GuestServicesPanelProps {
  data: DashboardResponse;
}

const styles = {
  container: {
    padding: 'clamp(16px, 4vw, 48px)',
    overflow: 'hidden' as const,
    height: '100%',
    boxSizing: 'border-box' as const,
    background: '#0d1b12',
    color: '#ffffff',
    fontFamily: "'Inter', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'clamp(8px, 2vw, 24px)',
  },
  heading: {
    fontSize: 'clamp(24px, 4vw, 48px)',
    color: '#c9a84c',
    margin: 0,
    fontWeight: 700,
  },
  columnsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'clamp(16px, 4vw, 48px)',
    flex: 1,
    overflow: 'hidden' as const,
  },
  column: {
    flex: 1,
    overflow: 'hidden' as const,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  sectionTitle: {
    fontSize: 'clamp(16px, 2.5vw, 28px)',
    color: '#c9a84c',
    margin: '0 0 12px 0',
    fontWeight: 600,
  },
  card: {
    padding: '12px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  cardTitle: {
    fontSize: 'clamp(14px, 2vw, 24px)',
    color: '#ffffff',
    fontWeight: 600,
    margin: 0,
  },
  cardBody: {
    fontSize: 'clamp(12px, 1.7vw, 20px)',
    color: '#e0e0e0',
    margin: '4px 0 0 0',
    lineHeight: 1.4,
  },
  vendorCategory: {
    fontSize: '18px',
    color: '#c9a84c',
    margin: '2px 0 0 0',
  },
  mealName: {
    fontSize: 'clamp(14px, 2vw, 24px)',
    color: '#ffffff',
    fontWeight: 600,
    margin: 0,
  },
  mealDescription: {
    fontSize: 'clamp(12px, 1.7vw, 20px)',
    color: '#e0e0e0',
    margin: '4px 0 0 0',
  },
  emptyText: {
    fontSize: 'clamp(14px, 1.8vw, 22px)',
    color: '#e0e0e0',
    fontStyle: 'italic' as const,
  },
};

/**
 * Guest Services area panel — shows announcements, meal options, and vendor highlights.
 */
export function GuestServicesPanel({ data }: GuestServicesPanelProps) {
  const gsData = data as GuestServicesDashboardData;

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>
        <ConciergeBell size={42} style={{ marginRight: 16, verticalAlign: 'middle' }} />
        Guest Services
      </h1>

      <div style={styles.columnsRow}>
        <div style={styles.column}>
          <h2 style={styles.sectionTitle}>
            <Megaphone size={24} style={{ marginRight: 10, verticalAlign: 'middle' }} />
            Announcements
          </h2>
          {gsData.announcements.length > 0 ? (
            gsData.announcements.map((announcement) => (
              <div key={announcement.id} style={styles.card}>
                <p style={styles.cardTitle}>{announcement.title}</p>
                <p style={styles.cardBody}>{announcement.body}</p>
              </div>
            ))
          ) : (
            <p style={styles.emptyText}>No announcements</p>
          )}
        </div>

        <div style={styles.column}>
          <h2 style={styles.sectionTitle}>
            <UtensilsCrossed size={24} style={{ marginRight: 10, verticalAlign: 'middle' }} />
            Meal Options
          </h2>
          {gsData.mealOptions.length > 0 ? (
            gsData.mealOptions.map((meal) => (
              <div key={meal.id} style={styles.card}>
                <p style={styles.mealName}>{meal.name}</p>
                <p style={styles.mealDescription}>{meal.description}</p>
              </div>
            ))
          ) : (
            <p style={styles.emptyText}>No meal options available</p>
          )}

          <h2 style={{ ...styles.sectionTitle, marginTop: '24px' }}>
            <Store size={24} style={{ marginRight: 10, verticalAlign: 'middle' }} />
            Vendor Highlights
          </h2>
          {gsData.vendors.length > 0 ? (
            gsData.vendors.map((vendor) => (
              <div key={vendor.id} style={styles.card}>
                <p style={styles.cardTitle}>{vendor.businessName}</p>
                {(vendor.category || vendor.boothNumber) && (
                  <p style={styles.vendorCategory}>
                    {vendor.category}
                    {vendor.category && vendor.boothNumber && ' • '}
                    {vendor.boothNumber && `Booth ${vendor.boothNumber}`}
                  </p>
                )}
              </div>
            ))
          ) : (
            <p style={styles.emptyText}>No vendors listed</p>
          )}
        </div>
      </div>
    </div>
  );
}
